import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {cnb} from 'cnbuilder';
import mixpanel from 'mixpanel-browser';
import IconCheckCircleDefault from '@teladoc/pulse/icons/check-circle-default.svg';
import IconArrowRightDefault from '@teladoc/pulse/icons/arrow-right-default.svg';
import Link from '@teladoc/fe-ccm/ui/router/Link';
import {useTransLoader} from '@teladoc/fe-i18n';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import {CONDITION_MANAGEMENT, SIDEBAR_MENU} from '@/config';
import CommonUtils from '@/utils/common-utils';
import {MENTAL_HEALTH_PREFIX, YOUR_CASES_PREFIX} from '@/router/routes';
import OneAppUserAPI from '@/user/user-oneapp-api';
import {useFepConfig} from '../utils/use-fep-config';
import css from './Navigation.scss';

const NavLink = ({
    id,
    path,
    Icon,
    content,
    ariaRole,
    showSideBar,
    mixpanelObj,
    amplitudeProperty,
    customAmplitudeProperty,
    onItemClick,
    iconClassName,
    itemClassName,
    setHeaderTitle,
    showNavigationIcon,
    activeItemClassName,
    testAttr,
    ...rest
}) => {
    const {t} = useTransLoader('app');
    const {programs = [], userLocale} = useSelector(state => state.user);
    const {branding} = useSelector(state => state.app);
    const countryValue = CommonUtils.getCountry(userLocale);
    const {isFepEnabled, isMinorDependent} = useFepConfig();

    const onNavItemClick = evt => {
        if (id === 'logout') {
            evt.preventDefault();
        }

        if (onItemClick) {
            onItemClick(id);

            if (setHeaderTitle) {
                if (showSideBar) {
                    setHeaderTitle(t(`sidebar.navigation.${id}`));
                }

                if (
                    id === 'conditionManagement' ||
                    id === 'conditionManagementSidebar'
                ) {
                    setHeaderTitle(t(`sidebar.title.conditionManagement`));
                }
            }

            mixpanel.track(mixpanelObj?.event, mixpanelObj?.property);

            if (customAmplitudeProperty?.event) {
                const eventName =
                    TRACK_EVENTS_MAP[customAmplitudeProperty.event];

                if (eventName) {
                    AmplitudeClient.track(eventName, {
                        category: customAmplitudeProperty?.category,
                        // eslint-disable-next-line camelcase
                        product_line: customAmplitudeProperty?.productLine,
                    });
                }
            }

            if (amplitudeProperty) {
                AmplitudeClient.track(
                    TRACK_EVENTS_MAP.navigationButtonClicked,
                    {
                        category: 'navigation',
                        level: amplitudeProperty?.level,
                        button: amplitudeProperty?.button,
                        // eslint-disable-next-line camelcase
                        product_line: amplitudeProperty?.productLine,
                    }
                );
            }

            if (id === SIDEBAR_MENU.Weight) {
                evt.preventDefault();
            }
        }

        // logout redirects to the login host, so it must come after the
        // tracking calls above or the sign-out events never get sent
        if (id === 'logout') {
            OneAppUserAPI.logout(programs, branding?.member_host);
        }
    };

    const CCMActivePrograms = programs?.filter(
        route =>
            route.program !== 'BEHAVIORAL_HEALTH' && route.status === 'ACTIVE'
    );

    const renderCCMActiveProgram = evt => {
        return CCMActivePrograms.map(({slug}) => {
            return (
                <span key={slug} className={css.navProgramContent}>
                    <IconCheckCircleDefault className={css.navProgramIcon} />
                    {t(`header.programs.${slug}`)}
                </span>
            );
        });
    };

    const renderMenuItem = evt => {
        if (id === CONDITION_MANAGEMENT && CCMActivePrograms?.length === 1) {
            const programName = CCMActivePrograms[0].slug;

            return t(`header.navigation.${programName}`);
        }

        if (path.includes(MENTAL_HEALTH_PREFIX)) {
            return t('header.navigation.mentalHealth');
        }

        if (path.includes(YOUR_CASES_PREFIX)) {
            return t(`header.navigation.${id}_${countryValue}`);
        }

        if (id === 'coverage' && isFepEnabled && isMinorDependent) {
            return t(`header.navigation.billing`);
        }

        return t(`header.navigation.${id}`);
    };

    const renderContent = () => {
        if (!content) {
            return null;
        }

        let contentText = path.includes(YOUR_CASES_PREFIX)
            ? t(`header.subnavigation.${content}_${countryValue}`)
            : t(`header.subnavigation.${content}`);

        if (isFepEnabled && isMinorDependent && content === 'familyContent') {
            contentText = t(`header.subnavigation.manageAuthorizedConsenters`);
        }

        return <span className={css.navItemContent}>{contentText}</span>;
    };

    return (
        <Link
            id={id}
            className={cnb(css.navItem, itemClassName)}
            activeClassName={cnb(css.navItemActive, activeItemClassName)}
            to={
                path?.includes('health-insights-report')
                    ? {
                          pathname: path,
                          state: {backToPath: window.location.pathname},
                      }
                    : path
            }
            data-testid={testAttr}
            onClick={onNavItemClick}
            role={ariaRole}
            {...rest}
        >
            {showSideBar ? (
                <>
                    {Icon && (
                        <Icon
                            className={cnb(css.navItemIcon, css.navCCMIcon)}
                        />
                    )}
                    {t(`sidebar.navigation.${id}`)}
                    {showNavigationIcon && (
                        <IconArrowRightDefault
                            className={cnb(
                                css.navRightIcon,
                                css.navSidebarIcon
                            )}
                        />
                    )}
                </>
            ) : (
                <div className={css.navContainer}>
                    {Icon && (
                        <Icon className={cnb(css.navItemIcon, iconClassName)} />
                    )}
                    <div className={css.navTextContainer}>
                        {renderMenuItem()}
                        {renderContent()}
                        {CCMActivePrograms?.length > 1 &&
                            id === CONDITION_MANAGEMENT &&
                            renderCCMActiveProgram()}
                    </div>
                    {showNavigationIcon && (
                        <IconArrowRightDefault className={css.navRightIcon} />
                    )}
                </div>
            )}
        </Link>
    );
};

NavLink.propTypes = {
    id: PropTypes.string,
    path: PropTypes.string,
    Icon: PropTypes.elementType,
    showNavigationIcon: PropTypes.bool,
    ariaRole: PropTypes.string,
    content: PropTypes.string,
    onItemClick: PropTypes.func,
    iconClassName: PropTypes.string,
    activeItemClassName: PropTypes.string,
    itemClassName: PropTypes.string,
    setHeaderTitle: PropTypes.func,
    showSideBar: PropTypes.bool,
    mixpanelObj: PropTypes.object,
    amplitudeProperty: PropTypes.object,
    testAttr: PropTypes.string,
    customAmplitudeProperty: PropTypes.shape({
        event: PropTypes.string,
        category: PropTypes.string,
        productLine: PropTypes.string,
    }),
};

export default NavLink;
